import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { CURRENCIES, CURRENCY_CODES, DEFAULT_CURRENCY, detectCurrencyFromBrowser, LANGUAGE_CODES } from "../i18n/config";

const LocaleContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_KEY = "adwiz_currency";

function initialCurrency() {
  const saved = localStorage.getItem(CURRENCY_KEY);
  return CURRENCY_CODES.includes(saved) ? saved : detectCurrencyFromBrowser();
}

async function savePreferences(body) {
  try {
    await fetch(`${API}/auth/preferences`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.error("Failed to save locale preferences:", err);
  }
}

export function LocaleProvider({ children }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [currency, setCurrencyState] = useState(initialCurrency);
  const [syncedUserId, setSyncedUserId] = useState(null);

  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  // When a user logs in, adopt their saved preferences (or persist the current ones if none saved yet).
  useEffect(() => {
    if (!user || user.user_id === syncedUserId) return;
    setSyncedUserId(user.user_id);
    const updates = {};
    if (user.language && LANGUAGE_CODES.includes(user.language) && user.language !== language) {
      i18n.changeLanguage(user.language);
    } else if (!user.language) {
      updates.language = language;
    }
    if (user.currency && CURRENCY_CODES.includes(user.currency)) {
      if (user.currency !== currency) {
        setCurrencyState(user.currency);
        localStorage.setItem(CURRENCY_KEY, user.currency);
      }
    } else {
      updates.currency = currency;
    }
    if (Object.keys(updates).length) savePreferences(updates);
  }, [user, syncedUserId, language, currency, i18n]);

  const setLanguage = useCallback((code) => {
    if (!LANGUAGE_CODES.includes(code)) return;
    i18n.changeLanguage(code);
    if (user) savePreferences({ language: code });
  }, [i18n, user]);

  const setCurrency = useCallback((code) => {
    if (!CURRENCY_CODES.includes(code)) return;
    setCurrencyState(code);
    localStorage.setItem(CURRENCY_KEY, code);
    if (user) savePreferences({ currency: code });
  }, [user]);

  const currencyInfo = useMemo(
    () => CURRENCIES.find((c) => c.code === currency) || CURRENCIES.find((c) => c.code === DEFAULT_CURRENCY),
    [currency]
  );

  const formatPrice = useCallback((usd, opts = {}) => {
    const value = Number(usd || 0) * currencyInfo.rate;
    const decimals = opts.compact && Number.isInteger(Number((value).toFixed(currencyInfo.decimals))) ? 0 : currencyInfo.decimals;
    let number;
    try {
      number = new Intl.NumberFormat(`${language}-u-nu-latn`, { minimumFractionDigits: decimals, maximumFractionDigits: currencyInfo.decimals }).format(value);
    } catch {
      number = value.toFixed(decimals);
    }
    // Deterministic symbol placement across locales/browsers: "$49" / "£38.71" / "15.019 KD"
    return currencyInfo.symbol.length === 1 ? `${currencyInfo.symbol}${number}` : `${number} ${currencyInfo.symbol}`;
  }, [currencyInfo, language]);

  // Format an amount already expressed in a given currency (e.g. a stored transaction) with that currency's decimals.
  const formatAmount = useCallback((amount, code) => {
    const info = CURRENCIES.find((c) => c.code === code);
    const decimals = info ? info.decimals : 2;
    let number;
    try {
      number = new Intl.NumberFormat(`${language}-u-nu-latn`, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(Number(amount || 0));
    } catch {
      number = Number(amount || 0).toFixed(decimals);
    }
    return `${number} ${code || ""}`.trim();
  }, [language]);

  const value = useMemo(() => ({
    language, setLanguage, currency, currencyInfo, setCurrency, formatPrice, formatAmount, currencies: CURRENCIES,
  }), [language, setLanguage, currency, currencyInfo, setCurrency, formatPrice, formatAmount]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
